import { useState } from 'react'
import { CARRIERS, CARRIER_LABELS, WarehouseView } from '@/console/api/types'
import { Button } from '@/console/ui/Button'
import { Field } from '@/console/ui/Field'
import { Tag } from '@/console/ui/Tag'
import { PolicyCard, SaveErrorCallout } from './PolicyCard'
import {
  CarrierChoice,
  hasParcelCarrierIssues,
  initialParcelCarrierForm,
  isConfigured,
  isParcelCarrierDirty,
  NO_CARRIER,
  newShipperRow,
  ParcelCarrierForm,
  parcelCarrierIssues,
  toParcelCarrierPolicy,
} from './policyForm'
import { usePolicySave, WriteCallbacks } from './usePolicySave'
import styles from './Warehouse.module.css'

const CarrierOptions = (): JSX.Element => (
  <>
    {CARRIERS.map((carrier) => (
      <option key={carrier} value={carrier}>
        {CARRIER_LABELS[carrier]} ({carrier})
      </option>
    ))}
  </>
)

/**
 * LOGISPRD-15602 — PARCEL_CARRIER. 기본 택배사(선택 안 함 허용) + 화주별 택배사 표.
 *
 * 검증 오류는 해당 행 바로 아래에 보이고 저장을 잠근다. 새로 추가한 빈 행은 손대기 전까지 오류를 숨긴다(추가하자마자 빨간 글자가 뜨지 않게) —
 * 저장 잠금은 숨김과 무관하게 걸린다. 서버 값과 같으면(정규화 비교) 저장할 이유가 없어 잠근다.
 * 서버 값이 바뀌면 부모가 `key` 로 리마운트해 폼을 서버 값으로 리셋한다. 전송값 규칙(키 생략 · trim)은 `toParcelCarrierPolicy`.
 */
export const ParcelCarrierCard = ({
  view,
  warehouseCode,
  ...callbacks
}: WriteCallbacks & { view: WarehouseView; warehouseCode: string }): JSX.Element => {
  const [form, setForm] = useState<ParcelCarrierForm>(() => initialParcelCarrierForm(view))
  // 서버에서 온 행은 처음부터 오류를 보인다. 새 행은 한 번이라도 고치면 보인다.
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set(form.shippers.map((row) => row.key)))
  const { saving, error, save } = usePolicySave(warehouseCode, callbacks)

  const issues = parcelCarrierIssues(form)
  const invalid = hasParcelCarrierIssues(issues)
  const dirty = isParcelCarrierDirty(view, form)

  const updateRow = (key: string, patch: Partial<{ code: string; carrier: CarrierChoice }>): void => {
    setForm((prev) => ({ ...prev, shippers: prev.shippers.map((row) => (row.key === key ? { ...row, ...patch } : row)) }))
    setRevealed((prev) => new Set(prev).add(key))
  }

  const revert = (): void => {
    const initial = initialParcelCarrierForm(view)
    setForm(initial)
    setRevealed(new Set(initial.shippers.map((row) => row.key)))
  }

  return (
    <PolicyCard
      title="택배사"
      typeName="PARCEL_CARRIER"
      tags={isConfigured(view, 'PARCEL_CARRIER') ? null : <Tag tone="warn">미설정</Tag>}
      error={error ? <SaveErrorCallout error={error} /> : null}
      footer={
        <>
          <Button variant="line" size="sm" disabled={saving || !dirty} onClick={revert}>
            되돌리기
          </Button>
          <Button size="sm" disabled={saving || invalid || !dirty} onClick={() => save(toParcelCarrierPolicy(form))}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <div className={styles.parcelDefault}>
        <Field label="기본 택배사">
          <select
            value={form.carrier}
            disabled={saving}
            onChange={(e) => setForm((prev) => ({ ...prev, carrier: e.target.value as CarrierChoice }))}
          >
            <option value={NO_CARRIER}>선택 안 함</option>
            <CarrierOptions />
          </select>
        </Field>
      </div>

      <div className={styles.subHead}>
        <span className={styles.subTitle}>화주별 택배사</span>
        <span className={styles.subCount}>{form.shippers.length}건</span>
      </div>

      {form.shippers.length === 0 ? (
        <div className={styles.emptyRows}>화주별 지정이 없어요.</div>
      ) : (
        <table className={styles.shipperTable}>
          <thead>
            <tr>
              <th>화주코드</th>
              <th>택배사</th>
              <th aria-label="삭제" />
            </tr>
          </thead>
          <tbody>
            {form.shippers.map((row, index) => {
              const rowIssues = revealed.has(row.key) ? issues.rows[index] ?? [] : []
              return (
                <tr key={row.key} className={rowIssues.length > 0 ? styles.shipperRowInvalid : undefined}>
                  <td>
                    <input
                      type="text"
                      aria-label={`화주코드 ${index + 1}`}
                      aria-invalid={rowIssues.length > 0}
                      placeholder="화주코드"
                      spellCheck={false}
                      value={row.code}
                      disabled={saving}
                      onChange={(e) => updateRow(row.key, { code: e.target.value })}
                    />
                    {rowIssues.length > 0 ? (
                      <ul className={styles.rowIssues}>
                        {rowIssues.map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                      </ul>
                    ) : null}
                  </td>
                  <td>
                    <select
                      aria-label={`화주 택배사 ${index + 1}`}
                      value={row.carrier}
                      disabled={saving}
                      onChange={(e) => updateRow(row.key, { carrier: e.target.value as CarrierChoice })}
                    >
                      <option value={NO_CARRIER}>택배사 선택</option>
                      <CarrierOptions />
                    </select>
                  </td>
                  <td className={styles.shipperDel}>
                    <Button
                      variant="ghost"
                      disabled={saving}
                      onClick={() => setForm((prev) => ({ ...prev, shippers: prev.shippers.filter((r) => r.key !== row.key) }))}
                    >
                      삭제
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <div className={styles.addRow}>
        <Button
          variant="line"
          size="sm"
          disabled={saving}
          onClick={() => setForm((prev) => ({ ...prev, shippers: [...prev.shippers, newShipperRow()] }))}
        >
          + 화주 추가
        </Button>
      </div>

      {dirty && issues.form.length > 0 ? <p className={styles.formIssue}>{issues.form.join(' ')}</p> : null}
    </PolicyCard>
  )
}
